import {
  MAX_RECENT_ITEMS,
  type Creative,
  type CreativeVersion,
  type DemoState,
  type Draft,
  Status,
  createInitialState,
  editTargets,
  formatOptionFor,
  formats,
  imageGenerationModes,
  talentImages,
  templateImages,
  tones,
} from "./mock-data.js";

const STORAGE_KEY = "senden-pro-demo-state";
const NEW_PROMO_PATH = "/promo-images/new";
const state = loadInitialState();

interface GenerateImageResponse {
  imageDataUrl?: string;
  model?: string;
  error?: string;
}

interface CopyAssistResponse {
  draft?: Partial<Draft>;
  model?: string;
  error?: string;
}

interface InputImageItem {
  previewUrl: string;
  label: string;
  remove: string;
}

function loadInitialState(): DemoState {
  const initial = createInitialState();
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}") as Partial<DemoState>;
    const restored = {
      ...initial,
      ...saved,
      draft: {
        ...initial.draft,
        ...saved.draft,
      },
      basePreviewUrl: "",
      baseImageDataUrl: "",
      isGenerating: false,
      isSuggestingCopy: false,
      generationError: "",
      copyAssistError: "",
      referencePreviewUrls: [],
      referenceImageDataUrls: [],
    };
    return {
      ...restored,
      draft: window.location.pathname === NEW_PROMO_PATH ? emptyDraft() : restored.draft,
      selectedTarget: includesString(editTargets, restored.selectedTarget) ? restored.selectedTarget : initial.selectedTarget,
      selectedFormat: includesString(formats, restored.selectedFormat) ? restored.selectedFormat : initial.selectedFormat,
      selectedTone: includesString(tones, restored.selectedTone) ? restored.selectedTone : initial.selectedTone,
      useHighQualityImageModel: Boolean(restored.useHighQualityImageModel),
      isAdvancedSettingsOpen: Boolean(restored.isAdvancedSettingsOpen),
      isTemplatePanelOpen:
        saved.isTemplatePanelOpen === undefined ? initial.isTemplatePanelOpen : Boolean(saved.isTemplatePanelOpen),
      isTalentPanelOpen:
        saved.isTalentPanelOpen === undefined ? initial.isTalentPanelOpen : Boolean(saved.isTalentPanelOpen),
      includeImageText: saved.includeImageText === undefined ? initial.includeImageText : Boolean(saved.includeImageText),
    };
  } catch {
    return initial;
  }
}

function emptyDraft(): Draft {
  return {
    brief: "",
    productName: "",
    appeal: "",
    copy: "",
    audience: "",
    instruction: "",
    brandStyle: "",
  };
}

function includesString(values: readonly string[], value: string): boolean {
  return values.includes(value);
}

function persistState(): void {
  const {
    selectedTarget,
    selectedFormat,
    selectedTone,
    useHighQualityImageModel,
    isAdvancedSettingsOpen,
    isTemplatePanelOpen,
    isTalentPanelOpen,
    includeImageText,
    selectedCreativeId,
    selectedVersionId,
    draft,
    creatives,
  } = state;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        selectedTarget,
        selectedFormat,
        selectedTone,
        useHighQualityImageModel,
        isAdvancedSettingsOpen,
        isTemplatePanelOpen,
        isTalentPanelOpen,
        includeImageText,
        selectedCreativeId,
        selectedVersionId,
        draft,
        creatives,
      }),
    );
  } catch {
    // Generated image data URLs can exceed browser storage quota; in-memory state still works for the demo session.
  }
}

function navigate(path: string): void {
  if (path === NEW_PROMO_PATH && window.location.pathname !== NEW_PROMO_PATH) {
    resetNewPromoForm();
  }
  window.history.pushState({}, "", path);
  render();
}

function resetNewPromoForm(): void {
  state.draft = emptyDraft();
  state.generationError = "";
  state.copyAssistError = "";
  state.isAdvancedSettingsOpen = false;
  state.isTemplatePanelOpen = false;
  state.isTalentPanelOpen = false;
  persistState();
}

function currentCreative(): Creative | undefined {
  return state.creatives.find((creative) => creative.id === state.selectedCreativeId);
}

function currentVersion(): CreativeVersion | undefined {
  const creative = currentCreative();
  if (!creative) return undefined;
  return (
    creative.versions.find((version) => version.id === state.selectedVersionId) ??
    creative.versions.find((version) => version.id === creative.currentVersionId) ??
    creative.versions[0]
  );
}

function statusBadge(status: Status): string {
  const labels = {
    [Status.READY]: "READY",
    [Status.GENERATING]: "GENERATING",
    [Status.FAILED]: "FAILED",
  };
  return `<span class="badge ${status.toLowerCase()}">${labels[status]}</span>`;
}

function shell(content: string, activePath: string): string {
  const navItems = [
    { label: "ダッシュボード", short: "概要", icon: "⌂", path: "/dashboard", active: activePath === "/dashboard" },
    { label: "宣伝画像作成", short: "作成", icon: "+", path: "/promo-images/new", active: activePath === "/promo-images/new" },
    { label: "生成履歴", short: "履歴", icon: "◧", path: "/promo-images/history", active: activePath === "/promo-images/history" },
  ];
  return `
    <div class="shell">
      <aside class="side">
        <div class="brand">
          <span class="brand-mark">✧</span>
          <span><strong>senden</strong>-pro</span>
        </div>
        <nav class="nav" aria-label="メインナビゲーション">
          ${navItems
            .map(
              (item) => `
                <button type="button" class="${item.active ? "active" : ""}" data-nav="${item.path}" data-short="${item.short}">
                  <span aria-hidden="true">${item.icon}</span>
                  <span>${item.label}</span>
                </button>
              `,
            )
            .join("")}
        </nav>
      </aside>
      <main class="main">${content}</main>
    </div>
  `;
}

function pageHead(eyebrow: string, title: string, lead: string, actions = ""): string {
  return `
    <div class="page-head">
      <div>
        <p class="eyebrow">${eyebrow}</p>
        <h1>${title}</h1>
        <p class="lead">${lead}</p>
      </div>
      ${actions ? `<div class="actions">${actions}</div>` : ""}
    </div>
  `;
}

function emptyState(title: string, body: string, action = ""): string {
  return `
    <div class="empty-state card card-pad">
      <h2>${title}</h2>
      <p class="lead">${body}</p>
      ${action ? `<div class="actions" style="margin-top: 16px;">${action}</div>` : ""}
    </div>
  `;
}

function renderDashboard(): string {
  const recent = state.creatives.slice(0, MAX_RECENT_ITEMS);
  const readyCount = state.creatives.filter((item) => item.status === Status.READY).length;
  const failedCount = state.creatives.filter((item) => item.status === Status.FAILED).length;
  const generatingCount = state.creatives.filter((item) => item.status === Status.GENERATING).length;
  return shell(
    `
      ${pageHead(
        "Dashboard",
        "宣伝画像ダッシュボード",
        "AI宣伝画像生成の状況を確認できます",
        `<button class="btn" data-nav="/promo-images/new">+ 新規作成</button>
         <button class="btn secondary" data-nav="/promo-images/history">履歴を見る</button>`,
      )}
      <section class="metric-row">
        <div class="metric card">
          <span class="metric-icon purple">▣</span>
          <p>今回の生成数</p>
          <strong>${state.creatives.length}</strong><small>枚</small>
          <em>AI生成結果のみ集計</em>
        </div>
        <div class="metric card">
          <span class="metric-icon green">✓</span>
          <p>生成成功</p>
          <strong>${readyCount}</strong><small>件</small>
          <em>保存済み画像</em>
        </div>
        <div class="metric card">
          <span class="metric-icon blue">□</span>
          <p>編集中</p>
          <strong>${generatingCount}</strong><small>件</small>
          <em>処理中タスク</em>
        </div>
        <div class="metric card">
          <span class="metric-icon orange">!</span>
          <p>要確認</p>
          <strong>${failedCount}</strong><small>件</small>
          <em>失敗・再生成候補</em>
        </div>
      </section>
      <section class="dashboard-grid single">
        <div class="card card-pad">
          <div class="section-head">
            <h2>最近の生成</h2>
            ${recent.length > 0 ? `<button class="link-btn" data-nav="/promo-images/history">すべて見る</button>` : ""}
          </div>
          ${
            recent.length > 0
              ? `<div class="history-list">${recent.map(historyRow).join("")}</div>`
              : emptyState(
                  "生成履歴はまだありません",
                  "新規作成からAI生成を実行すると、結果がここに追加されます。",
                  `<button class="btn" data-nav="/promo-images/new">+ 新規作成</button>`,
                )
          }
        </div>
      </section>
    `,
    "/dashboard",
  );
}

function renderCreate(): string {
  return shell(
    `
      <div class="create-head">
        <div>
          <h1>画像を入れて、<span class="keep-word">宣伝画像を自動生成</span></h1>
          <p class="lead">商品写真・施術写真・参考画像から、<span class="keep-word">SNSや広告に使える画像</span>を作成します。</p>
        </div>
        <button class="btn secondary" data-nav="/promo-images/history">生成履歴を見る</button>
      </div>
      <section class="editor-grid create-grid">
        ${formPanel("新規生成", "AI で宣伝画像を生成", "生成する")}
        ${createPreviewPanel()}
      </section>
    `,
    "/promo-images/new",
  );
}

function renderEdit(): string {
  const creative = currentCreative();
  const version = currentVersion();
  if (!creative || !version) {
    return shell(
      `
        ${pageHead("Edit", "宣伝画像編集", "編集できる生成画像がありません。")}
        ${emptyState("生成画像がありません", "先に新規作成からAI生成を実行してください。", `<button class="btn" data-nav="/promo-images/new">+ 新規作成</button>`)}
      `,
      "/promo-images/new",
    );
  }
  return shell(
    `
      ${pageHead(
        "Edit",
        "宣伝画像編集",
        "既存画像を元に、部分修正や全体修正を重ねて version を作成します。",
        `<button class="btn secondary" data-nav="/promo-images/history">履歴へ戻る</button>
         <button class="btn" data-nav="/promo-images/${creative.id}/preview">プレビュー</button>`,
      )}
      <section class="editor-grid edit-grid grid">
        ${formPanel("再編集", "選択中 version を元に再生成", "再生成する")}
        <div class="grid">
          ${previewPanel(escapeHtml(creative.title), version.image, state.isGenerating)}
          <div class="card">
            <div class="version-rail" aria-label="バージョン履歴">
              ${creative.versions
                .map(
                  (item) => `
                    <button type="button" class="version-thumb ${item.id === version.id ? "active" : ""}" data-version="${item.id}">
                      <img src="${item.image}" alt="version ${item.number}" />
                      <span>v${item.number}${creative.currentVersionId === item.id ? " 採用" : ""}</span>
                    </button>
                  `,
                )
                .join("")}
            </div>
          </div>
          <div class="actions">
            <button class="btn" data-adopt-version="${version.id}" ${version.status !== Status.READY ? "disabled" : ""}>この版を採用</button>
            <button class="btn secondary" data-download="${version.image}">保存</button>
          </div>
        </div>
      </section>
    `,
    `/promo-images/${creative.id}/edit`,
  );
}

function formPanel(mode: string, title: string, submitLabel: string): string {
  const isCreate = mode === "新規生成";
  const advancedToggleLabel = state.isAdvancedSettingsOpen ? "こだわり設定を閉じる" : "こだわり設定";
  return `
    <div class="form-stack ${isCreate ? "create-form-stack" : ""}">
      ${
        isCreate
          ? ""
          : `<div class="section-title">
              <h2>${title}</h2>
              <p class="lead">選択中の画像を元に再編集します</p>
            </div>`
      }
      <form class="form" id="promo-form">
        <div class="step-card card card-pad">
          <div class="step-head ${isCreate ? "plain" : ""}">${isCreate ? "" : "<span>1</span>"}<h3>画像を追加</h3></div>
          <label class="drop">
            <input type="file" accept="image/png,image/jpeg,image/webp" id="base-image" multiple />
            <span class="drop-icon">↥</span>
            <strong>画像をドラッグ＆ドロップ</strong>
            <span>または <b>クリックして選択</b></span>
            <small>人物・商品・背景・参考画像など、複数枚を素材として追加できます。</small>
          </label>
          ${uploadThumbs()}
          ${templateImageGrid()}
          ${talentImageGrid()}
        </div>
        <div class="step-card card card-pad">
          <div class="step-head step-head-between">
            <div class="step-label">${isCreate ? "" : "<span>2</span>"}<h3>どんな画像を作りますか？</h3></div>
            <button class="btn secondary small" type="button" data-copy-assist ${state.isSuggestingCopy ? "disabled" : ""}>
              ${state.isSuggestingCopy ? "入力補助中" : "AIで下書き"}
            </button>
          </div>
          <div class="field">
            <label for="brief">商品・サービスのメモ</label>
            <textarea id="brief" name="brief" placeholder="例: 飲食店向けに、Instagram用の季節限定メニュー告知を作りたい">${escapeHtml(state.draft.brief)}</textarea>
          </div>
          <div class="field-grid">
            <div class="field">
              <label for="product-name">宣伝対象名</label>
              <input id="product-name" name="productName" value="${escapeAttr(state.draft.productName)}" placeholder="例: 春限定スキンケアセット" />
            </div>
            <div class="field">
              <label for="copy">入れたいテキスト</label>
              <input id="copy" name="copy" value="${escapeAttr(state.draft.copy)}" placeholder="例: 透明感、続く" />
            </div>
          </div>
          <div class="field">
            <label for="appeal">訴求文</label>
            <textarea id="appeal" name="appeal" placeholder="例: 季節の肌悩みに寄り添う新作ラインを、清潔感のあるビジュアルで訴求したい">${escapeHtml(state.draft.appeal)}</textarea>
          </div>
          <div class="field">
            <label for="audience">ターゲット</label>
            <input id="audience" name="audience" value="${escapeAttr(state.draft.audience)}" placeholder="例: 美容感度が高い20代後半から40代女性" />
          </div>
          <p class="assist-note">AIが内容を整理して、最適な画像を自動で作成します。</p>
          ${
            state.copyAssistError
              ? `<p class="error-message" role="alert">${escapeHtml(state.copyAssistError)}</p>`
              : ""
          }
        </div>
        <div class="step-card card card-pad output-settings-card">
          <div class="step-head ${isCreate ? "plain" : ""}">${isCreate ? "" : "<span>3</span>"}<h3>出力設定</h3></div>
          <div class="output-settings-grid">
            <div class="field compact-field">
              <label>使い道を選択</label>
              <div class="option-grid compact-option-grid">
                ${formats.map((format) => optionChip(format, state.selectedFormat === format, "format")).join("")}
              </div>
            </div>
            <div class="field compact-field">
              <label>トーン（雰囲気）を選択</label>
              <div class="chip-row compact-chip-row">
                ${tones.map((tone) => chip(tone, state.selectedTone === tone, "tone")).join("")}
              </div>
              <label class="toggle-card main-quality-toggle">
                <span>
                  <strong>高品質モデルで生成</strong>
                  <small>${imageGenerationModes.highQuality.label} / ${imageGenerationModes.highQuality.model}</small>
                </span>
                <input type="checkbox" id="high-quality-image-model-main" ${state.useHighQualityImageModel ? "checked" : ""} />
              </label>
              <button type="button" class="chip control-chip ${state.isAdvancedSettingsOpen ? "active" : ""}" data-open-advanced aria-expanded="${state.isAdvancedSettingsOpen ? "true" : "false"}">${advancedToggleLabel}</button>
            </div>
          </div>
        </div>
        ${instructionStep()}
        ${state.isAdvancedSettingsOpen ? advancedSettingsDrawer() : ""}
        ${
          state.generationError
            ? `<p class="error-message" role="alert">${escapeHtml(state.generationError)}</p>`
            : ""
        }
        <div class="sticky-actions">
          <button class="btn" type="submit" ${state.isGenerating ? "disabled" : ""}>${state.isGenerating ? "生成中" : isCreate ? "この内容で生成する" : `✧ ${submitLabel}`}</button>
          <button class="btn secondary" type="button" data-save-draft>下書き保存</button>
        </div>
      </form>
    </div>
  `;
}

function instructionStep(): string {
  const selectedImages = inputImageItems();
  const hasMultipleImages = selectedImages.length > 1;
  return `
    <div class="step-card card card-pad">
      <div class="step-head"><span>6</span><h3>${hasMultipleImages ? "素材の使い方" : "詳細指示"}</h3></div>
      ${
        hasMultipleImages
          ? `<div class="material-usage">
              <div class="material-usage-head">
                <strong>選択した画像の順番</strong>
                <span>${selectedImages.length}枚</span>
              </div>
              <div class="material-usage-list">
                ${selectedImages
                  .map(
                    (item, index) => `
                      <div class="material-usage-item">
                        <img src="${escapeAttr(item.previewUrl)}" alt="${escapeAttr(`${index + 1}枚目 ${item.label}`)}" />
                        <span>${index + 1}枚目</span>
                      </div>
                    `,
                  )
                  .join("")}
              </div>
            </div>`
          : ""
      }
      <div class="field">
        <label for="instruction">${hasMultipleImages ? "画像の組み合わせ指示" : "細かい編集指示"}</label>
        <textarea id="instruction" name="instruction" placeholder="${hasMultipleImages ? "例: 1枚目の人物に、2枚目の商品を自然に持たせてください。3枚目は背景の雰囲気だけ参考にしてください。" : ""}">${escapeHtml(state.draft.instruction)}</textarea>
      </div>
    </div>
  `;
}

function chip(label: string, active: boolean, group: string): string {
  return `<button type="button" class="chip ${active ? "active" : ""}" data-chip-group="${group}" data-chip-value="${label}">${label}</button>`;
}

function optionChip(label: string, active: boolean, group: string): string {
  const format = formatOptionFor(label);
  return `<button type="button" class="option-chip ${active ? "active" : ""}" data-chip-group="${group}" data-chip-value="${label}">
    <span class="format-icon ${format.icon}">${formatIcon(format.icon)}</span>
    <strong>${escapeHtml(label)}</strong>
    <span>${formatSizeLabel(label)}</span>
  </button>`;
}

function formatIcon(icon: string): string {
  if (icon === "line") return "LINE";
  if (icon === "web") return "▣";
  return "◎";
}

function formatSizeLabel(label: string): string {
  return formatOptionFor(label).displaySize;
}

function advancedSettingsDrawer(): string {
  return `
    <div class="advanced-backdrop" data-close-advanced></div>
    <aside class="advanced-drawer" role="dialog" aria-modal="true" aria-labelledby="advanced-settings-title">
      <div class="advanced-drawer-head">
        <div>
          <p class="eyebrow">Optional</p>
          <h2 id="advanced-settings-title">こだわり設定</h2>
          <p>こだわりを設定すると、よりご希望に近い画像を生成できます。</p>
        </div>
        <button type="button" class="icon-btn" data-close-advanced-button aria-label="こだわり設定を閉じる">×</button>
      </div>
      <div class="advanced-drawer-body">
        <div class="field">
          <label for="advanced-audience">ターゲット</label>
          <input id="advanced-audience" name="audience" value="${escapeAttr(state.draft.audience)}" placeholder="例: 20〜30代女性 / 美容・健康に関心が高い人" />
        </div>
        <div class="field">
          <label>出力サイズ</label>
          <div class="advanced-size-grid">
            ${formats.map((format) => optionChip(format, state.selectedFormat === format, "format")).join("")}
          </div>
        </div>
        <div class="field">
          <label>編集対象</label>
          <div class="advanced-chip-grid advanced-chip-scroll">
            ${editTargets.map((target) => chip(target, state.selectedTarget === target, "target")).join("")}
          </div>
        </div>
        <div class="field">
          <label>トーン（雰囲気）</label>
          <div class="advanced-chip-grid">
            ${tones.map((tone) => chip(tone, state.selectedTone === tone, "tone")).join("")}
          </div>
        </div>
        <div class="field">
          <div class="advanced-field-head">
            <label>参考画像 <span>任意</span></label>
            <button type="button" class="link-btn" data-clear-reference-images>すべてクリア</button>
          </div>
          ${uploadThumbs()}
        </div>
        <label class="toggle-card advanced-toggle">
          <span>
            <strong>画像内テキスト</strong>
            <small>コピー・タイトルなどを画像内に含める</small>
          </span>
          <input type="checkbox" id="include-image-text" ${state.includeImageText ? "checked" : ""} />
        </label>
        <label class="toggle-card advanced-toggle">
          <span>
            <strong>高品質モデルで生成</strong>
            <small>${imageGenerationModes.highQuality.label} / ${imageGenerationModes.highQuality.model}</small>
          </span>
          <input type="checkbox" id="high-quality-image-model" ${state.useHighQualityImageModel ? "checked" : ""} />
        </label>
        <div class="field">
          <label for="advanced-instruction">詳細指示 <span>任意</span></label>
          <textarea id="advanced-instruction" name="instruction" placeholder="例: 人物は右側、商品は中央に大きく配置。透明感のある光でお願いします。">${escapeHtml(state.draft.instruction)}</textarea>
        </div>
        <div class="field">
          <label for="brand-style">ブランド・雰囲気の指定 <span>任意</span></label>
          <input id="brand-style" name="brandStyle" value="${escapeAttr(state.draft.brandStyle)}" placeholder="例: 水色・白・レモンイエロー / 清潔感 / 爽やか" />
        </div>
      </div>
      <div class="advanced-drawer-actions">
        <button type="button" class="btn secondary" data-close-advanced>キャンセル</button>
        <button type="button" class="btn" data-apply-advanced>この設定を反映</button>
      </div>
    </aside>
  `;
}

function uploadThumbs(): string {
  const items = inputImageItems();
  return `
    <div class="upload-thumbs" aria-label="追加画像">
      ${items
        .map(
          (item) => `
            <div class="upload-thumb">
              <img src="${escapeAttr(item.previewUrl)}" alt="${escapeAttr(item.label)}" />
              <button type="button" data-remove-image="${escapeAttr(item.remove)}" aria-label="${escapeAttr(item.label)}を削除">×</button>
            </div>
          `,
        )
        .join("")}
      <button type="button" class="upload-add" data-trigger-upload aria-label="画像を追加">+</button>
    </div>
  `;
}

function templateImageGrid(): string {
  const selectedTemplateCount = templateImages.filter((image) => isTemplateSelected(image.src)).length;
  const toggleLabel = state.isTemplatePanelOpen ? "画像テンプレートを閉じる" : "画像テンプレートを開く";
  return `
    <div class="template-image-block">
      <button type="button" class="template-image-toggle" data-toggle-template-images aria-expanded="${state.isTemplatePanelOpen ? "true" : "false"}" aria-controls="template-image-grid">
        <span>
          <strong>テンプレートを素材として選択</strong>
          <small>${selectedTemplateCount > 0 ? `${selectedTemplateCount}件選択中` : `${templateImages.length}件`}</small>
        </span>
        <b aria-hidden="true">${state.isTemplatePanelOpen ? "−" : "+"}</b>
        <span class="sr-only">${toggleLabel}</span>
      </button>
      <div id="template-image-grid" class="template-image-grid ${state.isTemplatePanelOpen ? "" : "hidden"}" aria-label="宣伝画像テンプレート">
        ${
          state.isTemplatePanelOpen
            ? templateImages
                .map((image) => {
                  const isSelected = isTemplateSelected(image.src);
                  return `
                    <button type="button" class="template-image-card ${isSelected ? "active" : ""}" data-template-src="${escapeAttr(image.src)}" aria-pressed="${isSelected ? "true" : "false"}" aria-label="${escapeAttr(image.alt)}">
                      <img src="${escapeAttr(image.src)}" alt="${escapeAttr(image.alt)}" />
                      ${isSelected ? `<span class="template-selected-mark" aria-hidden="true">✓</span>` : ""}
                    </button>
                  `;
                })
                .join("")
            : ""
        }
      </div>
    </div>
  `;
}

function talentImageGrid(): string {
  const selectedTalentCount = talentImages.filter((image) => isTalentSelected(image.src)).length;
  const toggleLabel = state.isTalentPanelOpen ? "所属タレントを閉じる" : "所属タレントを開く";
  return `
    <div class="template-image-block talent-image-block">
      <button type="button" class="template-image-toggle talent-image-toggle" data-toggle-talent-images aria-expanded="${state.isTalentPanelOpen ? "true" : "false"}" aria-controls="talent-image-grid">
        <span>
          <strong>所属タレントを選択</strong>
          <small>${selectedTalentCount > 0 ? `${selectedTalentCount}件選択中` : `${talentImages.length}件 / 有料プラン`}</small>
        </span>
        <b aria-hidden="true">${state.isTalentPanelOpen ? "−" : "+"}</b>
        <span class="sr-only">${toggleLabel}</span>
      </button>
      <div id="talent-image-grid" class="template-image-grid talent-image-grid ${state.isTalentPanelOpen ? "" : "hidden"}" aria-label="所属タレント">
        ${
          state.isTalentPanelOpen
            ? talentImages
                .map((image) => {
                  const isSelected = isTalentSelected(image.src);
                  return `
                    <button type="button" class="template-image-card talent-image-card ${isSelected ? "active" : ""}" data-talent-src="${escapeAttr(image.src)}" aria-pressed="${isSelected ? "true" : "false"}" aria-label="${escapeAttr(image.alt)}">
                      <img src="${escapeAttr(image.src)}" alt="${escapeAttr(image.alt)}" />
                      <span class="talent-price-badge">${escapeHtml(image.priceLabel)}</span>
                      ${isSelected ? `<span class="template-selected-mark" aria-hidden="true">✓</span>` : ""}
                    </button>
                  `;
                })
                .join("")
            : ""
        }
      </div>
    </div>
  `;
}

function inputImageItems(): InputImageItem[] {
  return [
    ...(state.basePreviewUrl ? [{ previewUrl: state.basePreviewUrl, label: "テンプレート画像", remove: "base" }] : []),
    ...state.referencePreviewUrls.map((previewUrl, index) => ({
      previewUrl,
      label: referenceImageLabel(previewUrl, index),
      remove: `reference:${index}`,
    })),
  ];
}

function inputImageCount(): number {
  return inputImageItems().length;
}

function isTemplateSelected(src: string): boolean {
  return state.basePreviewUrl === src || state.referencePreviewUrls.includes(src);
}

function isTalentSelected(src: string): boolean {
  return state.referencePreviewUrls.includes(src);
}

function referenceImageLabel(previewUrl: string, index: number): string {
  const talentIndex = talentImages.findIndex((image) => image.src === previewUrl);
  if (talentIndex >= 0) return `所属タレント ${talentIndex + 1}`;
  const templateIndex = templateImages.findIndex((image) => image.src === previewUrl);
  if (templateIndex >= 0) return `テンプレート画像 ${templateIndex + 1}`;
  return `参照画像 ${index + 1}`;
}

function createPreviewPanel(): string {
  const primaryPreviewUrl = state.basePreviewUrl || state.referencePreviewUrls[0] || "";
  const hasImage = Boolean(primaryPreviewUrl);
  return `
    <aside class="create-preview card card-pad">
      <div class="preview-title-row">
        <h2>プレビュー</h2>
        ${state.isGenerating ? statusBadge(Status.GENERATING) : hasImage ? statusBadge(Status.READY) : ""}
      </div>
      <div class="preview-frame create-preview-frame ${hasImage ? "contain" : ""}">
        ${
          hasImage
            ? `<img src="${escapeAttr(primaryPreviewUrl)}" alt="生成プレビュー" />`
            : `<div class="create-preview-empty">
                <div class="empty-illustration">▧</div>
                <strong>ここに生成結果が表示されます</strong>
                <span>画像と内容を入力して「この内容で生成する」を押してください</span>
              </div>`
        }
        <div class="overlay ${state.isGenerating ? "" : "hidden"}" role="status" aria-label="生成中">
          <div>
            <div class="spinner"></div>
            <p style="margin-top: 12px; font-weight: 800;">AIで宣伝画像を生成しています</p>
          </div>
        </div>
      </div>
      ${hasImage ? `<div class="preview-variants">${uploadThumbs()}</div>` : ""}
      <div class="preview-actions">
        <button class="btn secondary" data-download="${escapeAttr(primaryPreviewUrl)}" ${hasImage ? "" : "disabled"}>保存する</button>
        <button class="btn secondary" data-nav="/promo-images/history">編集へ進む</button>
        <button class="btn secondary" data-generate-from-form ${hasImage ? "" : "disabled"}>別パターンを生成</button>
      </div>
    </aside>
  `;
}

function previewPanel(title: string, image: string, generating: boolean): string {
  return `
    <div class="card card-pad">
      <div class="page-head">
        <div>
          <h2>${title}</h2>
          <p class="lead">${state.selectedFormat} / ${state.selectedTone} / ${state.selectedTarget}</p>
        </div>
        ${statusBadge(generating ? Status.GENERATING : Status.READY)}
      </div>
      <div class="preview-frame contain">
        ${
          image
            ? `<img src="${image}" alt="生成プレビュー" />`
            : `<div class="preview-empty">アップロード画像または生成画像がここに表示されます。</div>`
        }
        <div class="overlay ${generating ? "" : "hidden"}" role="status" aria-label="生成中">
          <div>
            <div class="spinner"></div>
            <p style="margin-top: 12px; font-weight: 800;">AIで宣伝画像を生成しています</p>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderHistory(): string {
  const dialog = state.dialogVersionId ? detailDialog() : "";
  return shell(
    `
      ${pageHead(
        "History",
        "生成履歴",
        "AIで生成した宣伝画像と、入力条件・version を確認できます。",
        `<button class="btn" data-nav="/promo-images/new">+ 新規作成</button>`,
      )}
      ${
        state.creatives.length > 0
          ? `<section class="history-grid">${state.creatives.map(historyTile).join("")}</section>`
          : emptyState("生成履歴はまだありません", "生成を実行すると、ここから詳細確認や再編集に進めます。")
      }
      ${dialog}
    `,
    "/promo-images/history",
  );
}

function historyRow(creative: Creative): string {
  const version = creative.versions.find((item) => item.id === creative.currentVersionId) ?? creative.versions[0];
  if (!version) return "";
  return `
    <div class="history-row">
      <img src="${version.image}" alt="${escapeAttr(creative.title)}" />
      <div>
        <strong>${escapeHtml(creative.title)}</strong>
        <span>${escapeHtml(creative.usage)} ・ ${formatSizeLabel(creative.format)}</span>
      </div>
      <span class="badge ${creative.status.toLowerCase()}">${creative.status === Status.READY ? "完了" : creative.status}</span>
      <button class="icon-btn" data-nav="/promo-images/${creative.id}/edit" aria-label="編集する">✎</button>
    </div>
  `;
}

function historyTile(creative: Creative): string {
  const version = creative.versions.find((item) => item.id === creative.currentVersionId) ?? creative.versions[0];
  if (!version) return "";
  return `
    <button type="button" class="tile card" data-history="${version.id}">
      <div class="tile-image"><img src="${version.image}" alt="${escapeAttr(creative.title)}" /></div>
      <div class="tile-body">
        <h3>${escapeHtml(creative.title)}</h3>
        <p class="lead">${escapeHtml(creative.usage)}</p>
        <div class="tile-meta">
          ${statusBadge(creative.status)}
          <span class="badge">${escapeHtml(creative.format)}</span>
          <span class="badge">v${version.number}</span>
        </div>
      </div>
    </button>
  `;
}

function settingsSummary(): string {
  return `
    <div class="card card-pad summary-card">
      <h2>生成設定のまとめ</h2>
      <dl>
        <div><dt>出力形式</dt><dd>${formatSizeLabel(state.selectedFormat)}</dd></div>
        <div><dt>トーン</dt><dd>${escapeHtml(state.selectedTone)}</dd></div>
        <div><dt>編集対象</dt><dd>${escapeHtml(state.selectedTarget)}</dd></div>
        <div><dt>生成モデル</dt><dd>${escapeHtml(selectedImageMode().label)}</dd></div>
      </dl>
    </div>
  `;
}

function selectedImageMode() {
  return state.useHighQualityImageModel ? imageGenerationModes.highQuality : imageGenerationModes.standard;
}

function aiHintPanel(): string {
  return `
    <div class="card card-pad hint-card">
      <h2>AIからのヒント</h2>
      <ul>
        <li>商品のアップで質感やディテールを強調する</li>
        <li>季節感を演出する小物や背景を取り入れる</li>
        <li>視線の流れを意識したレイアウトにする</li>
      </ul>
    </div>
  `;
}

function detailDialog(): string {
  const match = findVersion(state.dialogVersionId);
  if (!match) return "";
  const { creative, version } = match;
  return `
    <div class="dialog-backdrop" data-close-dialog>
      <div class="dialog" role="dialog" aria-modal="true" aria-label="履歴詳細" data-dialog>
        <div class="dialog-grid">
          <div class="dialog-image"><img src="${version.image}" alt="${escapeAttr(creative.title)}" /></div>
          <aside class="card-pad">
            <div class="page-head">
              <div>
                <p class="eyebrow">Version ${version.number}</p>
                <h2>${escapeHtml(creative.title)}</h2>
              </div>
              <button class="btn ghost" data-close-dialog-button>閉じる</button>
            </div>
            <p class="lead">${escapeHtml(creative.appeal)}</p>
            <div class="grid" style="margin-top: 16px;">
              <div>${statusBadge(version.status)} <span class="badge">${escapeHtml(creative.format)}</span></div>
              <p><strong>編集対象:</strong> ${escapeHtml(version.target)}</p>
              <p><strong>詳細指示:</strong> ${escapeHtml(version.instruction)}</p>
              <p><strong>トーン:</strong> ${escapeHtml(creative.tone)}</p>
              <p><strong>作成日時:</strong> ${escapeHtml(version.createdAt)}</p>
            </div>
            <div class="actions" style="margin-top: 20px;">
              <button class="btn secondary" data-download="${version.image}">保存</button>
              <button class="btn secondary" data-recreate="${creative.id}">この条件で再作成</button>
              <button class="btn" data-edit="${creative.id}">この画像を元に再編集</button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  `;
}

function renderPreview(): string {
  const creative = currentCreative();
  const version = currentVersion();
  if (!creative || !version) {
    return shell(
      `
        ${pageHead("Preview", "画像プレビュー", "プレビューできる生成画像がありません。")}
        ${emptyState("生成画像がありません", "先に新規作成からAI生成を実行してください。", `<button class="btn" data-nav="/promo-images/new">+ 新規作成</button>`)}
      `,
      "/promo-images/new",
    );
  }
  return shell(
    `
      ${pageHead(
        "Preview",
        "画像プレビュー",
        "生成画像を大きく確認し、保存または編集へ戻れます。",
        `<button class="btn secondary" data-nav="/promo-images/${creative.id}/edit">編集へ戻る</button>
         <button class="btn" data-download="${version.image}">保存</button>`,
      )}
      <section class="split">
        <div class="card card-pad">
          <div class="preview-frame contain"><img src="${version.image}" alt="大きなプレビュー" /></div>
        </div>
        <aside class="card card-pad">
          <p class="eyebrow">Selected</p>
          <h2>${escapeHtml(creative.title)}</h2>
          <p class="lead">${escapeHtml(creative.usage)}</p>
          <div class="grid" style="margin-top: 16px;">
            <p><strong>出力形式:</strong> ${escapeHtml(creative.format)}</p>
            <p><strong>トーン:</strong> ${escapeHtml(creative.tone)}</p>
            <p><strong>バージョン:</strong> ${version.number}</p>
            <p><strong>編集対象:</strong> ${escapeHtml(version.target)}</p>
          </div>
        </aside>
      </section>
    `,
    `/promo-images/${creative.id}/preview`,
  );
}

function findVersion(versionId: string): { creative: Creative; version: CreativeVersion } | undefined {
  for (const creative of state.creatives) {
    const version = creative.versions.find((item) => item.id === versionId);
    if (version) return { creative, version };
  }
  return undefined;
}

function setDraftFromForm(form: HTMLFormElement): void {
  const data = new FormData(form);
  state.draft = {
    brief: formValue(data, "brief", state.draft.brief),
    productName: formValue(data, "productName", state.draft.productName),
    appeal: formValue(data, "appeal", state.draft.appeal),
    copy: formValue(data, "copy", state.draft.copy),
    audience: formValue(data, "audience", state.draft.audience),
    instruction: formValue(data, "instruction", state.draft.instruction),
    brandStyle: formValue(data, "brandStyle", state.draft.brandStyle),
  };
}

function formValue(data: FormData, name: string, fallback: string): string {
  const values = data.getAll(name).filter((value): value is string => typeof value === "string");
  return values.at(-1) ?? fallback;
}

function syncDraftFromCurrentForm(): void {
  const form = document.getElementById("promo-form");
  if (form instanceof HTMLFormElement) setDraftFromForm(form);
}

async function runGeneration(form: HTMLFormElement): Promise<void> {
  setDraftFromForm(form);
  if (inputImageCount() > 1 && state.draft.instruction.trim().length === 0) {
    state.generationError =
      "複数画像を使う場合は、1枚目の何を使って2枚目以降をどう組み合わせるかを入力してください。";
    render();
    return;
  }
  const path = window.location.pathname;
  const isCreate = path.includes("/new");
  const creative = currentCreative();
  const sourceVersion = currentVersion();
  state.generationError = "";
  state.isGenerating = true;
  render();

  try {
    const response = await fetch("/api/generate-image", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...state.draft,
        format: state.selectedFormat,
        tone: state.selectedTone,
        target: state.selectedTarget,
        brandStyle: state.draft.brandStyle,
        includeImageText: state.includeImageText,
        useHighQualityImageModel: state.useHighQualityImageModel,
        baseImageDataUrl: state.baseImageDataUrl,
        referenceImageDataUrls: state.referenceImageDataUrls,
        sourceImageDataUrl: isCreate ? "" : sourceVersion?.image ?? "",
      }),
    });
    const payload = (await response.json().catch(() => ({}))) as GenerateImageResponse;
    if (!response.ok || !payload.imageDataUrl) {
      throw new Error(payload.error || "画像生成に失敗しました。");
    }
    if (isCreate || !creative) {
      addCreative(payload.imageDataUrl, payload.model ?? selectedImageMode().model);
    } else {
      addVersion(creative, payload.imageDataUrl, payload.model ?? selectedImageMode().model);
    }
    state.isGenerating = false;
    navigate(`/promo-images/${state.selectedCreativeId}/edit`);
  } catch (error) {
    state.isGenerating = false;
    state.generationError = error instanceof Error ? error.message : "画像生成に失敗しました。";
    render();
  }
}

async function runCopyAssist(form: HTMLFormElement): Promise<void> {
  setDraftFromForm(form);
  state.copyAssistError = "";
  if (!hasCopyAssistSource()) {
    state.copyAssistError = "AI下書きには、商品・サービスのメモか宣伝対象名などの材料を入力してください。";
    render();
    return;
  }
  state.isSuggestingCopy = true;
  render();

  try {
    const response = await fetch("/api/suggest-copy", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...state.draft,
        format: state.selectedFormat,
        tone: state.selectedTone,
        target: state.selectedTarget,
      }),
    });
    const payload = (await response.json().catch(() => ({}))) as CopyAssistResponse;
    if (!response.ok || !payload.draft) {
      throw new Error(payload.error || "入力補助に失敗しました。");
    }
    state.draft = {
      brief: payload.draft.brief ?? state.draft.brief,
      productName: payload.draft.productName ?? state.draft.productName,
      appeal: payload.draft.appeal ?? state.draft.appeal,
      copy: payload.draft.copy ?? state.draft.copy,
      audience: payload.draft.audience ?? state.draft.audience,
      instruction: payload.draft.instruction ?? state.draft.instruction,
      brandStyle: payload.draft.brandStyle ?? state.draft.brandStyle,
    };
    state.isSuggestingCopy = false;
    persistState();
    render();
  } catch (error) {
    state.isSuggestingCopy = false;
    state.copyAssistError = error instanceof Error ? error.message : "入力補助に失敗しました。";
    render();
  }
}

function hasCopyAssistSource(): boolean {
  return [
    state.draft.brief,
    state.draft.productName,
    state.draft.appeal,
    state.draft.copy,
    state.draft.audience,
    state.draft.instruction,
  ].some((value) => value.trim().length > 0);
}

function addCreative(image: string, model: string): void {
  const id = `creative-${Date.now()}`;
  const versionId = `${id}-v1`;
  const createdAt = new Date().toLocaleString("ja-JP");
  state.creatives.unshift({
    id,
    title: state.draft.productName || "新しい宣伝画像",
    appeal: state.draft.appeal,
    usage: state.selectedFormat,
    format: state.selectedFormat,
    tone: state.selectedTone,
    status: Status.READY,
    createdAt,
    currentVersionId: versionId,
    versions: [
      {
        id: versionId,
        number: 1,
        image,
        model,
        mode: selectedImageMode().label,
        status: Status.READY,
        target: state.selectedTarget,
        instruction: state.draft.instruction,
        createdAt,
      },
    ],
  });
  state.selectedCreativeId = id;
  state.selectedVersionId = versionId;
  persistState();
}

function addVersion(creative: Creative, image: string, model: string): void {
  const versionId = `${creative.id}-v${creative.versions.length + 1}`;
  const version = {
    id: versionId,
    number: creative.versions.length + 1,
    image,
    model,
    mode: selectedImageMode().label,
    status: Status.READY,
    target: state.selectedTarget,
    instruction: state.draft.instruction,
    createdAt: new Date().toLocaleString("ja-JP"),
  };
  creative.title = state.draft.productName || creative.title;
  creative.appeal = state.draft.appeal;
  creative.usage = state.selectedFormat;
  creative.format = state.selectedFormat;
  creative.tone = state.selectedTone;
  creative.versions.push(version);
  creative.currentVersionId = versionId;
  creative.status = Status.READY;
  state.selectedVersionId = versionId;
  persistState();
}

function closestElement(target: EventTarget | null, selector: string): HTMLElement | null {
  return target instanceof Element ? target.closest(selector) : null;
}

function handleClick(event: MouseEvent): void {
  const nav = closestElement(event.target, "[data-nav]");
  if (nav) {
    navigate(nav.dataset.nav ?? "/dashboard");
    return;
  }

  const generateFromFormButton = closestElement(event.target, "[data-generate-from-form]");
  if (generateFromFormButton) {
    const form = document.getElementById("promo-form");
    if (form instanceof HTMLFormElement) runGeneration(form);
    return;
  }

  const templateButton = closestElement(event.target, "[data-template-src]");
  if (templateButton) {
    syncDraftFromCurrentForm();
    void toggleAssetImage(templateButton.dataset.templateSrc ?? "");
    return;
  }

  const talentButton = closestElement(event.target, "[data-talent-src]");
  if (talentButton) {
    syncDraftFromCurrentForm();
    void toggleAssetImage(talentButton.dataset.talentSrc ?? "");
    return;
  }

  const templatePanelButton = closestElement(event.target, "[data-toggle-template-images]");
  if (templatePanelButton) {
    syncDraftFromCurrentForm();
    state.isTemplatePanelOpen = !state.isTemplatePanelOpen;
    persistState();
    render();
    return;
  }

  const talentPanelButton = closestElement(event.target, "[data-toggle-talent-images]");
  if (talentPanelButton) {
    syncDraftFromCurrentForm();
    state.isTalentPanelOpen = !state.isTalentPanelOpen;
    persistState();
    render();
    return;
  }

  const uploadTrigger = closestElement(event.target, "[data-trigger-upload]");
  if (uploadTrigger) {
    document.getElementById("base-image")?.click();
    return;
  }

  const removeImageButton = closestElement(event.target, "[data-remove-image]");
  if (removeImageButton) {
    syncDraftFromCurrentForm();
    removeInputImage(removeImageButton.dataset.removeImage ?? "");
    return;
  }

  const copyAssistButton = closestElement(event.target, "[data-copy-assist]");
  if (copyAssistButton) {
    const form = copyAssistButton.closest("#promo-form") ?? document.getElementById("promo-form");
    if (form instanceof HTMLFormElement) runCopyAssist(form);
    return;
  }

  const closeAdvanced =
    event.target instanceof Element &&
    (event.target.matches("[data-close-advanced]") || event.target.closest("[data-close-advanced-button]"));
  if (closeAdvanced) {
    state.isAdvancedSettingsOpen = false;
    persistState();
    render();
    return;
  }

  const applyAdvancedButton = closestElement(event.target, "[data-apply-advanced]");
  if (applyAdvancedButton) {
    const form = applyAdvancedButton.closest("#promo-form") ?? document.getElementById("promo-form");
    if (form instanceof HTMLFormElement) setDraftFromForm(form);
    state.isAdvancedSettingsOpen = false;
    persistState();
    render();
    return;
  }

  const clearReferenceImagesButton = closestElement(event.target, "[data-clear-reference-images]");
  if (clearReferenceImagesButton) {
    syncDraftFromCurrentForm();
    clearReferenceImages();
    return;
  }

  const advancedButton = closestElement(event.target, "[data-open-advanced]");
  if (advancedButton) {
    const form = advancedButton.closest("#promo-form");
    if (form instanceof HTMLFormElement) setDraftFromForm(form);
    state.isAdvancedSettingsOpen = !state.isAdvancedSettingsOpen;
    persistState();
    render();
    return;
  }

  const chipButton = closestElement(event.target, "[data-chip-group]");
  if (chipButton) {
    const form = chipButton.closest("#promo-form");
    if (form instanceof HTMLFormElement) setDraftFromForm(form);
    const group = chipButton.dataset.chipGroup;
    if (group === "format") state.selectedFormat = chipButton.dataset.chipValue ?? state.selectedFormat;
    if (group === "tone") state.selectedTone = chipButton.dataset.chipValue ?? state.selectedTone;
    if (group === "target") state.selectedTarget = chipButton.dataset.chipValue ?? state.selectedTarget;
    render();
    return;
  }

  const versionButton = closestElement(event.target, "[data-version]");
  if (versionButton) {
    state.selectedVersionId = versionButton.dataset.version ?? "";
    render();
    return;
  }

  const historyButton = closestElement(event.target, "[data-history]");
  if (historyButton) {
    state.dialogVersionId = historyButton.dataset.history ?? "";
    render();
    return;
  }

  const closeDialog =
    event.target instanceof Element &&
    (event.target.matches("[data-close-dialog]") || event.target.closest("[data-close-dialog-button]"));
  if (closeDialog) {
    state.dialogVersionId = "";
    render();
    return;
  }

  const editButton = closestElement(event.target, "[data-edit]");
  if (editButton) {
    state.selectedCreativeId = editButton.dataset.edit ?? "";
    state.dialogVersionId = "";
    const creative = currentCreative();
    state.selectedVersionId = creative?.currentVersionId ?? "";
    navigate(creative ? `/promo-images/${creative.id}/edit` : "/promo-images/new");
    return;
  }

  const recreateButton = closestElement(event.target, "[data-recreate]");
  if (recreateButton) {
    state.selectedCreativeId = recreateButton.dataset.recreate ?? "";
    state.dialogVersionId = "";
    const creative = currentCreative();
    if (creative) {
      state.draft.productName = creative.title;
      state.draft.appeal = creative.appeal;
      state.selectedFormat = creative.format;
      state.selectedTone = creative.tone;
    }
    navigate("/promo-images/new");
    return;
  }

  const adoptButton = closestElement(event.target, "[data-adopt-version]");
  if (adoptButton) {
    const creative = currentCreative();
    if (!creative) return;
    creative.currentVersionId = adoptButton.dataset.adoptVersion ?? creative.currentVersionId;
    state.selectedVersionId = creative.currentVersionId;
    persistState();
    render();
    return;
  }

  const downloadButton = closestElement(event.target, "[data-download]");
  if (downloadButton) {
    const link = document.createElement("a");
    link.href = downloadButton.dataset.download ?? "";
    link.download = "senden-pro-ai.png";
    link.click();
  }
}

async function handleChange(event: Event): Promise<void> {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (target.id === "high-quality-image-model" || target.id === "high-quality-image-model-main") {
    const form = target.closest("#promo-form");
    if (form instanceof HTMLFormElement) setDraftFromForm(form);
    state.useHighQualityImageModel = target.checked;
    persistState();
    render();
    return;
  }
  if (target.id === "include-image-text") {
    const form = target.closest("#promo-form");
    if (form instanceof HTMLFormElement) setDraftFromForm(form);
    state.includeImageText = target.checked;
    persistState();
    render();
    return;
  }
  if (target.id !== "base-image") return;
  const files = Array.from(target.files ?? []);
  if (files.length === 0) return;
  syncDraftFromCurrentForm();
  await addReferenceFiles(files);
  target.value = "";
  render();
}

function handleSubmit(event: SubmitEvent): void {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || form.id !== "promo-form") return;
  event.preventDefault();
  runGeneration(form);
}

async function toggleAssetImage(src: string): Promise<void> {
  if (!src) return;
  if (state.basePreviewUrl === src) {
    revokePreviewUrl(state.basePreviewUrl);
    state.basePreviewUrl = "";
    state.baseImageDataUrl = "";
    render();
    return;
  }
  const existingReferenceIndex = state.referencePreviewUrls.indexOf(src);
  if (existingReferenceIndex >= 0) {
    state.referencePreviewUrls.splice(existingReferenceIndex, 1);
    state.referenceImageDataUrls.splice(existingReferenceIndex, 1);
    render();
    return;
  }
  const response = await fetch(src);
  const blob = await response.blob();
  state.referencePreviewUrls.push(src);
  state.referenceImageDataUrls.push(await readFileAsDataURL(blob));
  render();
}

async function addReferenceFiles(files: File[]): Promise<void> {
  const additions = await Promise.all(
    files.map(async (file) => ({
      previewUrl: URL.createObjectURL(file),
      dataUrl: await readFileAsDataURL(file),
    })),
  );
  state.referencePreviewUrls.push(...additions.map((item) => item.previewUrl));
  state.referenceImageDataUrls.push(...additions.map((item) => item.dataUrl));
}

function removeInputImage(target: string): void {
  if (target === "base") {
    revokePreviewUrl(state.basePreviewUrl);
    state.basePreviewUrl = "";
    state.baseImageDataUrl = "";
    render();
    return;
  }

  const match = target.match(/^reference:(\d+)$/);
  if (!match) return;
  const index = Number(match[1]);
  const previewUrl = state.referencePreviewUrls[index];
  if (!previewUrl) return;
  revokePreviewUrl(previewUrl);
  state.referencePreviewUrls.splice(index, 1);
  state.referenceImageDataUrls.splice(index, 1);
  render();
}

function clearReferenceImages(): void {
  state.referencePreviewUrls.forEach(revokePreviewUrl);
  state.referencePreviewUrls = [];
  state.referenceImageDataUrls = [];
  render();
}

function revokePreviewUrl(previewUrl: string): void {
  if (previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
}

function readFileAsDataURL(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function render(): void {
  const path = window.location.pathname === "/" ? "/dashboard" : window.location.pathname;
  if (window.location.pathname === "/") {
    window.history.replaceState({}, "", path);
  }
  const app = document.getElementById("app");
  if (!app) return;
  if (path.includes("/promo-images/new")) {
    app.innerHTML = renderCreate();
  } else if (path.includes("/promo-images/history")) {
    app.innerHTML = renderHistory();
  } else if (path.includes("/preview")) {
    syncCreativeFromPath(path);
    app.innerHTML = renderPreview();
  } else if (path.includes("/edit")) {
    syncCreativeFromPath(path);
    app.innerHTML = renderEdit();
  } else {
    app.innerHTML = renderDashboard();
  }
}

function syncCreativeFromPath(path: string): void {
  const match = path.match(/promo-images\/([^/]+)/);
  if (!match) return;
  const creative = state.creatives.find((item) => item.id === match[1]);
  if (!creative) return;
  state.selectedCreativeId = creative.id;
  if (!creative.versions.some((version) => version.id === state.selectedVersionId)) {
    state.selectedVersionId = creative.currentVersionId;
  }
}

function escapeHtml(value: unknown): string {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

function escapeAttr(value: unknown): string {
  return escapeHtml(value).replaceAll('"', "&quot;");
}

window.addEventListener("popstate", render);
document.addEventListener("click", handleClick);
document.addEventListener("change", handleChange);
document.addEventListener("submit", handleSubmit);
render();
